import { FieldType, isEmptyOrWhitespace, ViewModelBase } from "@shoothill/core";
import type { ApiResult, ValidationResponse } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";

import { AppUrls } from "AppUrls";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { DetailsHeaderModel } from "Globals/Views/DetailsPage/DetailsHeaderModel";
import { VariationFutureSpendAmendmentDTO, VariationFutureSpendAmendmentAndRelatedResponseDTO, VariationFutureSpendAmendmentModel } from "./VariationFutureSpendAmendmentModel";
import { StoresInstance } from "Globals/Stores";
import { RoleLevelEnum } from "Globals/Stores/Domain/Admin";
import { ChangeEvent } from "react";

export class VariationFutureSpendAmendmentViewModel extends ViewModelBase<VariationFutureSpendAmendmentModel> {
    // #region Constructors and Disposers

    constructor(ieId: string, variationId: string, variationItemId: string) {
        super(new VariationFutureSpendAmendmentModel());
        this.setDecorators(VariationFutureSpendAmendmentModel);

        this.model.ieId = ieId;
        this.model.variationId = variationId;
        this.model.variationItemId = variationItemId;

        this.loadFutureSpendAndAmendment(variationId, variationItemId);
    }

    // #endregion Constructors and Disposers

    @observable
    private ieTitle: string = "";

    @action setIETitle = (val: string) => {
        this.ieTitle = val;
    };

    @computed
    public get getIETitle(): string {
        return this.ieTitle;
    }

    /**
     * Header
     */
    @computed
    get getHeader(): DetailsHeaderModel {
        const retVal: DetailsHeaderModel = new DetailsHeaderModel();

        if (this.categoryName === "" || this.subCategoryName === "" || this.lineDescriptionName === "") {
            retVal.setValue("title", "");
        } else {
            retVal.setValue("title", `${this.categoryName} > ${this.subCategoryName} > ${this.lineDescriptionName}`);
        }

        retVal.setValue("subTitle", "Future spend");

        return retVal;
    }

    @computed
    public get canAddFutureSpendAmendment(): boolean {
        // Only users with role CommDirector and above can add amendments.
        const roleLevel = StoresInstance.Domain.AccountStore.UserRoleLevel;
        return roleLevel === null || roleLevel >= RoleLevelEnum.CommDir;
    }

    @observable
    private variationFutureSpendAmendmentItems = observable<VariationFutureSpendAmendmentDTO>([]); // Should probably update to viewmodel.

    @computed
    public get variationFutureSpendAmendmentItemsFiltered(): VariationFutureSpendAmendmentDTO[] {
        if (this.filterNoImpactItems) {
            return this.variationFutureSpendAmendmentItems.filter(
                (i) => !(i.revision !== null && i.revision !== "" && i.orderValueDifference === 0 && i.previousFutureSpend === i.futureSpend),
            );
        }
        return this.variationFutureSpendAmendmentItems;
    }

    @observable
    public filterNoImpactItems: boolean = false;

    @action
    public setFilterNoImpactItems = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        this.filterNoImpactItems = checked;
    };

    @observable
    public showMatchingUnapprovedRequisitionsModal: boolean = false;

    @observable
    public matchingUnapprovedRequisitions: { id: string; name: string; requestedByUserName: string }[] = [];

    @action
    public handleShowMatchingUnapprovedRequisitionsModalChange(show: boolean) {
        this.showMatchingUnapprovedRequisitionsModal = show;

        if (!show) {
            this.setMatchingUnapprovedRequisitions([]);
        }
    }

    @action
    public setMatchingUnapprovedRequisitions(items: { id: string; name: string; requestedByUserName: string }[]) {
        this.matchingUnapprovedRequisitions = items;
    }

    @computed
    public get getShowMatchingUnapprovedRequisitionsModal() {
        return this.showMatchingUnapprovedRequisitionsModal;
    }

    // #region Properties

    public server: ServerViewModel = new ServerViewModel();

    // #endregion Properties

    // #region Actions

    @action
    public setNote = (val: string) => {
        this.model.note = val;
    };

    @observable private categoryName: string = "";
    @observable private subCategoryName: string = "";
    @observable private lineDescriptionName: string = "";

    @action
    public loadFutureSpendAndAmendment = (variationId: string, variationItemId: string): Promise<void> => {
        return this.server.query<VariationFutureSpendAmendmentAndRelatedResponseDTO>(
            () => this.Get(`${AppUrls.Server.Variation.GetVariationFutureSpendAmendmentAndRelated}\\${variationId}\\${variationItemId}`),
            (result) => {
                runInAction(() => {
                    this.variationFutureSpendAmendmentItems.replace(result.variationFutureSpendAmendmentItems);

                    this.categoryName = result.categoryName;
                    this.subCategoryName = result.subCategoryName;
                    this.lineDescriptionName = result.lineDescriptionName;

                    this.setIETitle(result.ieTitle);
                });
            },
        );
    };

    public handleSave = async (): Promise<void> => {
        if (this.canAddFutureSpendAmendment) {
            await this.save();
        }
    };

    private save = async (): Promise<ApiResult<VariationFutureSpendAmendmentAndRelatedResponseDTO>> => {
        let dto = this.model.toDto();

        const result = await this.Post<VariationFutureSpendAmendmentAndRelatedResponseDTO>(AppUrls.Server.Variation.UpsertVariationFutureSpendAmendment, dto).then((apiResult) => {
            if (!apiResult.wasSuccessful) {
                // console.log(apiResult.errors);
                runInAction(() => {
                    console.log(apiResult.errors);
                    this.setSnackMessage("There was an error trying to add the amendment");
                    this.setSnackType(this.SNACKERROR);
                    this.setSnackbarState(true);
                });

                if (apiResult.errors.length > 0) {
                    if (apiResult.errors[0].message === "Lines match existing unapproved requisition." && apiResult.payload.matchingUnapprovedRequisitions.length > 0) {
                        runInAction(() => {
                            this.handleShowMatchingUnapprovedRequisitionsModalChange(true);
                            this.setMatchingUnapprovedRequisitions(apiResult.payload.matchingUnapprovedRequisitions);
                        });
                    }
                }
            } else {
                runInAction(() => {
                    this.variationFutureSpendAmendmentItems.replace(apiResult.payload.variationFutureSpendAmendmentItems);

                    this.categoryName = apiResult.payload.categoryName;
                    this.subCategoryName = apiResult.payload.subCategoryName;
                    this.lineDescriptionName = apiResult.payload.lineDescriptionName;

                    this.model.reset();
                });
            }

            return apiResult;
        });

        return result;
    };

    // #endregion Actions

    @computed
    private get validateFutureSpend(): ValidationResponse {
        const errorMessage = this.model.validateFutureSpend;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateNote(): ValidationResponse {
        const errorMessage = this.model.validateNote;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    // #region Boilerplate

    public async isFieldValid(fieldName: keyof FieldType<VariationFutureSpendAmendmentModel>): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        if (this.server.IsSubmitted) {
            // Process the properties of the model that cannot be supported via
            // the use of decorators.
            switch (fieldName) {
                case "futureSpend": {
                    const result = this.validateFutureSpend;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }

                case "note": {
                    const result = this.validateNote;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
            }
        } else {
            // Do not validate if the properties of the model have not been
            // submitted to the server.
            errorMessage = "";
            isValid = true;
        }

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    @observable
    public snackbarState = false;

    @action
    public setSnackbarState = (val: boolean) => {
        this.snackbarState = val;
    };

    @observable
    public snackMessage = "";

    @action
    public setSnackMessage = (val: string) => {
        this.snackMessage = val;
    };

    @observable
    public snackType = "";

    @action
    public setSnackType = (val: string) => {
        this.snackType = val;
    };

    @observable
    public SNACKSUCCESS = "success";

    @observable
    public SNACKERROR = "error";

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    // #endregion Bolierplate
}
